import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Leaf } from 'lucide-react';
import { FOUNDER, CRUNCH_FLAVORS } from '../mock';

const About = () => {
  return (
    <main>
      {/* HERO */}
      <section className="bg-[#1a120b] text-[#f6efe1] py-24 md:py-32 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#b78d3c]/12 rounded-full blur-3xl"></div>
        <div className="container-lux relative z-10 max-w-4xl">
          <p className="tracking-luxe text-[#b78d3c] mb-4">The House</p>
          <h1 className="font-display text-6xl md:text-[92px] leading-[1.02] mb-8">
            A rare<br />
            <em className="font-serif-alt not-italic text-[#b78d3c]">chocolate house.</em>
          </h1>
          <p className="text-xl text-[#f6efe1]/75 leading-relaxed max-w-2xl">
            Raretto is a modern chocolate manufacturer from Mumbai, built on many generations of confection craftsmanship and one obsession, that India’s flavour deserved its own chocolate house.
          </p>
        </div>
      </section>

      
      {/* THE QUESTION */}
      <section className="section">
        <div className="container-lux max-w-4xl">
          <p className="tracking-luxe text-[#b78d3c] mb-6">The Question</p>
          <div className="font-display text-4xl md:text-6xl leading-[1.1] space-y-3">
            <p>Many generations understood ingredients.</p>
            <p>Many generations understood flavour.</p>
            <p>Many generations understood craftsmanship.</p>
            <p className="text-[#1a120b]/45 pt-4">This one asked a different question.</p>
            <p className="italic font-serif-alt pt-2">“What if India could be experienced through chocolate?”</p>
          </div>
          <p className="text-[#3a2a1e]/75 mt-12 leading-relaxed max-w-2xl">
            That question became Raretto. Not tradition repeated. Tradition reinterpreted, in a form India had not yet built for itself.
          </p>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="section bg-[#efe6d3]">
        <div className="container-lux grid md:grid-cols-[440px_1fr] gap-16 items-start">
          <div className="aspect-[4/5] bg-[#1a120b] relative overflow-hidden">
            {FOUNDER.image ? (
              <img src={FOUNDER.image} alt={FOUNDER.name} className="w-full h-full object-cover object-[center_15%]" />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-[#f6efe1]">
                <div className="w-36 h-36 rounded-full bg-[#f6efe1]/8 border border-[#b78d3c]/40 flex items-center justify-center mb-6">
                  <span className="font-display text-6xl text-[#b78d3c]">SR</span>
                </div>
                <p className="tracking-luxe text-[#b78d3c] text-[10px]">Portrait</p>
                <p className="font-display text-3xl mt-3">{FOUNDER.name}</p>
                <p className="text-xs text-[#f6efe1]/50 mt-2">Photograph forthcoming</p>
              </div>
            )}
          </div>

          <div>
            <p className="tracking-luxe text-[#b78d3c] mb-4">The Founder</p>
            <h2 className="font-display text-5xl md:text-6xl leading-tight mb-8">Shlok Rathi.</h2>

            <div className="space-y-5 text-[#3a2a1e]/90 leading-relaxed text-lg max-w-2xl">
              <p>Shlok is a second-generation entrepreneur from a family whose craft has always been confection. He grew up in workshops where flavour was measured by feel and consistency was earned, not manufactured.</p>
              <p>He could have continued the family form. He chose to build a new one.</p>
              <p>Raretto is his answer, a chocolate house that composes Indian ingredients to the standard of the world’s finest brands, and refuses to imitate any of them.</p>
            </div>

            <div className="gold-rule my-10 max-w-xs"></div>

            <p className="font-serif-alt italic text-2xl md:text-3xl text-[#1a120b] leading-snug max-w-2xl">
              “{FOUNDER.quote}”
            </p>
            <p className="tracking-luxe text-[#1a120b]/55 mt-4">{FOUNDER.name}</p>
          </div>
        </div>
      </section>

      {/* QUALITY & SOURCING */}
      <section className="section bg-[#1a120b] text-[#f6efe1]">
        <div className="container-lux">
          <div className="max-w-2xl mb-14">
            <p className="tracking-luxe text-[#b78d3c] mb-3">The Standard</p>
            <h2 className="font-display text-5xl md:text-6xl leading-tight">Sourcing decides everything.</h2>
            <p className="text-[#f6efe1]/70 mt-6 leading-relaxed">Exceptional chocolate begins long before production. Each ingredient is chosen where it grows best, and only where it grows best.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { t: 'Belgian couverture', d: 'The chocolate itself, unrefined for shortcut, tempered slowly for depth.' },
              { t: 'Piedmont hazelnut', d: 'Roasted whole in-house. Never bought pre-roasted, never pre-crushed.' },
              { t: 'Ratnagiri Alphonso', d: 'Preserved at the height of a short season. Colour is not corrected.' },
              { t: 'Chikmagalur espresso', d: 'Single-origin, medium-dark. Extracted the morning it goes in.' },
              { t: 'Sun-cured gulkand', d: 'Rose petals slowly matured in the sun, as India has done for centuries.' },
              { t: 'Whole-form nuts', d: 'Almond, cashew, pistachio, always graded, always fresh, never fragmented.' },
            ].map((x, i) => (
              <div key={i} className="border-t border-[#f6efe1]/20 pt-5">
                <p className="tracking-luxe text-[#b78d3c] mb-3">0{i + 1}</p>
                <p className="font-display text-2xl mb-3">{x.t}</p>
                <p className="text-[#f6efe1]/70 leading-relaxed text-sm">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDIAN FLAVOURS */}
      <section className="section">
        <div className="container-lux grid md:grid-cols-2 gap-16 items-center">
          <div>
            <p className="tracking-luxe text-[#b78d3c] mb-4">Flavour</p>
            <h2 className="font-display text-5xl md:text-6xl leading-tight mb-8">India, translated.</h2>
            <div className="space-y-5 text-[#3a2a1e]/85 leading-relaxed text-lg">
              <p>Paan. Gulkand. Alphonso. Coconut. Coffee. These are not novelty flavours. They are the language India has always spoken about sweetness, refinement and ceremony.</p>
              <p>Raretto composes them into chocolate not to be clever but because they are, quite simply, exceptional.</p>
              <p>The result is a house of flavour that could only have come from here.</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {CRUNCH_FLAVORS.slice(0, 9).map((f) => (
              <div key={f.id} className="aspect-square rounded-full bg-[#efe6d3] halo overflow-hidden group flex items-center justify-center">
                {f.image ? (
                  <img src={f.image} alt={f.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                ) : (
                  <div className="font-display text-lg text-center px-2" style={{ color: f.color }}>{f.name}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MUMBAI */}
      {/* <section className="section bg-[#efe6d3]">
        <div className="container-lux max-w-4xl text-center">
          <MapPin className="w-6 h-6 text-[#b78d3c] mx-auto mb-4" />
          <p className="tracking-luxe text-[#b78d3c] mb-6">The Atelier</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[1.05] mb-8">Composed in Mumbai.</h2>
          <p className="text-xl text-[#3a2a1e]/85 leading-relaxed max-w-2xl mx-auto">
            Designed, tempered and perfected in our Mumbai atelier before reaching chocolate lovers across India and, in time, the world.
          </p>
          <p className="text-[#3a2a1e]/60 mt-4 italic font-serif-alt text-lg">Mumbai is the beginning. Not the destination.</p>
        </div>
      </section> */}

      
    </main>
  );
};

export default About;
